/**
 * Server-side fetching for public store APIs (the WooCommerce Store API) —
 * an escalation ladder, kept free of Firebase so it can be tested. firebase.ts
 * wires it to the `fetchHtml` callable and the Cloudflare Worker proxy as
 * `fetchStoreApiText`.
 *
 * Most shops answer the default request (Celegin, WMT, Yema), so nothing
 * changes for them. Some hosts refuse it: bryanlaurent.com and
 * alexanderdauvin.com 403 the exact desktop-Chrome UA `fetchHtml` sends
 * (Chrome/131 — and Chrome/120; other versions and honest API clients get
 * through, reproduced from a residential IP on 2026-10-16). Only on such a
 * refusal does the ladder try the next request shape:
 *
 *   1. `default`    — fetchHtml as every other caller uses it.
 *   2. `api`        — fetchHtml with an honest API-client UA (profile 'api').
 *   3. `cloudflare` — the WK Cloudflare Worker proxy, a different egress.
 *   4. give up      — StoreApiBlockedError: the host is actively blocking us.
 *
 * A bot-CHALLENGE page served with a 2xx status (SiteGround's sgcaptcha from
 * Cloud Functions egress — 2026-10-16, after the 'api' rung went live) counts
 * as a refusal too, so it climbs the ladder instead of being taken as the
 * shop's answer.
 *
 * A rung that works is remembered per host, so a blocked shop costs the
 * failed attempts once per run, not on every page.
 */

export type FetchHtmlProfile = 'browser' | 'api';

/** A fetch that came back without a body; `status` 0 = unreachable. */
export class FetchHtmlError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'FetchHtmlError';
  }
}

/** Every rung of the ladder was refused by the host. */
export class StoreApiBlockedError extends Error {
  readonly blockedByHost = true;
  constructor(
    readonly host: string,
    readonly status: number,
    readonly attempts: string[],
  ) {
    super(
      `${host} is actively blocking our requests (HTTP ${status} on every attempt: ${attempts.join(', ')}). ` +
        'The dev team should look at a residential proxy option for this shop.',
    );
    this.name = 'StoreApiBlockedError';
  }
}

/** Statuses a bot filter answers with — the only ones that climb the ladder. */
const BLOCKED_STATUSES = new Set([401, 403, 406, 429]);

export function isBlockedError(err: unknown): err is FetchHtmlError {
  return err instanceof FetchHtmlError && BLOCKED_STATUSES.has(err.status);
}

/**
 * Bot-challenge / WAF interstitials served with a 2xx status instead of a
 * 403. SiteGround (bryanlaurent.com, alexanderdauvin.com) answers suspicious
 * traffic — datacenter egress like Cloud Functions — with a 202 page that
 * bounces through /.well-known/sgcaptcha/; Cloudflare, Incapsula, Sucuri,
 * PerimeterX, DDoS-Guard and Distil do the same. Matched on the body, since
 * the status says "OK".
 */
const CHALLENGE_SIGNATURES: Array<[RegExp, string]> = [
  [/sgcaptcha/i, 'SiteGround bot challenge'],
  [/cf-chl|challenge-platform|cf_chl_opt|<title>\s*just a moment/i, 'Cloudflare bot challenge'],
  [/attention required! \| cloudflare/i, 'Cloudflare block page'],
  [/_incapsula_resource|incapsula incident/i, 'Incapsula bot challenge'],
  [/sucuri_cloudproxy|sucuri website firewall/i, 'Sucuri firewall'],
  [/px-captcha|perimeterx/i, 'PerimeterX bot challenge'],
  [/ddos-guard/i, 'DDoS-Guard challenge'],
  [/pardon our interruption/i, 'Distil bot challenge'],
  [/checking your browser before accessing/i, 'browser check'],
];

/** The challenge a 2xx body is, if it is one. */
export function detectBotChallenge(body: string): string | null {
  const head = body.slice(0, 20000);
  if (/^\s*[[{]/.test(head)) return null; // JSON is never a challenge page
  for (const [pattern, name] of CHALLENGE_SIGNATURES) if (pattern.test(head)) return name;
  return null;
}

export interface StoreApiRung {
  name: string;
  fetch: (url: string) => Promise<string>;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return url;
  }
}

/**
 * The ladder over the given rungs, in order. A non-block failure (404,
 * unreachable, a parse problem upstream) is thrown as-is from the rung that
 * hit it — escalating would not change those.
 */
export function createEscalatingFetcher(rungs: StoreApiRung[]): (url: string) => Promise<string> {
  const startAt = new Map<string, number>();
  return async (url) => {
    const host = hostOf(url);
    const first = startAt.get(host) ?? 0;
    const tried: string[] = [];
    let lastBlocked: FetchHtmlError | undefined;
    for (let i = first; i < rungs.length; i++) {
      try {
        const text = await rungs[i].fetch(url);
        // A challenge page served with 200/202 is a block, not an answer:
        // without this it reads as "the site isn't WooCommerce".
        const challenge = detectBotChallenge(text);
        if (challenge) throw new FetchHtmlError(`${challenge} (served as a normal page)`, 403);
        startAt.set(host, i);
        return text;
      } catch (err) {
        if (!isBlockedError(err)) throw err;
        tried.push(rungs[i].name);
        lastBlocked = err;
      }
    }
    throw new StoreApiBlockedError(host, lastBlocked?.status ?? 403, tried);
  };
}

/** The standard ladder: fetchHtml default → fetchHtml api → Cloudflare Worker (if any). */
export function createStoreApiFetcher(
  fetchWithProfile: (url: string, options: { profile: FetchHtmlProfile }) => Promise<string>,
  viaCloudflare?: (url: string) => Promise<string>,
): (url: string) => Promise<string> {
  return createEscalatingFetcher([
    { name: 'standard request', fetch: (url) => fetchWithProfile(url, { profile: 'browser' }) },
    { name: 'API-client request', fetch: (url) => fetchWithProfile(url, { profile: 'api' }) },
    ...(viaCloudflare ? [{ name: 'Cloudflare proxy', fetch: viaCloudflare }] : []),
  ]);
}
